import React from 'react';
import { Card, Row, Col, ProgressBar, Alert, Form } from 'react-bootstrap';
import { FiletypeCsv } from 'react-bootstrap-icons';
import useReportsGallery from '../hooks/useReportsGallery';

const ReportsGallery = () => {
  const {
    selectedClientId,
    setSelectedClientId,
    clients,
    downloadCsvRequest,
    downloadClientCsvRequest,
    fetchSubsidiariesRequest,
    handleDownloadCsv,
    handleDownloadClientCsv,
    error,
  } = useReportsGallery();

  return (
    <>
      <style>
        {`
          .report-card {
            transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
            border: none !important;
            background: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(10px);
            overflow: hidden;
          }
          .report-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 12px 24px rgba(0,0,0,0.12) !important;
          }
          .report-card.loading {
            cursor: wait !important;
            pointer-events: none;
            opacity: 0.8;
          }
          .icon-wrapper {
            width: 80px;
            height: 80px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 20px;
            margin: 0 auto 1.5rem;
            transition: all 0.3s ease;
          }
          .report-card:hover .icon-wrapper {
            transform: scale(1.1) rotate(5deg);
          }
          .icon-primary { background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%); color: #0d6efd; }
          .icon-success { background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%); color: #198754; }
          
          .progress-label {
            font-size: 0.8rem;
            font-weight: 600;
            color: #6c757d;
            margin-top: 0.5rem;
            display: block;
          }
          .starting-badge {
            animation: pulse 1.5s infinite;
            color: #0d6efd;
            font-weight: bold;
          }
          @keyframes pulse {
            0% { opacity: 0.4; }
            50% { opacity: 1; }
            100% { opacity: 0.4; }
          }
        `}
      </style>
      <div className="mb-5">
        <h1 className="fw-bold mb-2">Relatórios</h1>
        <p className="text-muted fs-5">Acesse e exporte dados administrativos do sistema.</p>
      </div>

      {error && (
        <Alert
          variant="danger"
          className="shadow-sm border-0 mb-4"
          dismissible
          onClose={() => {
            downloadCsvRequest.reset();
            downloadClientCsvRequest.reset();
          }}
        >
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2" />
            <div>{error}</div>
          </div>
        </Alert>
      )}

      <Row className="g-4">
        <Col md={6} lg={4} xl={4}>
          <Card
            className={`h-100 shadow-sm report-card ${downloadCsvRequest.loading ? 'loading' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={handleDownloadCsv}
          >
            {downloadCsvRequest.loading && (
              <ProgressBar
                animated={downloadCsvRequest.progress < 100}
                now={downloadCsvRequest.progress}
                style={{
                  height: '6px',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                }}
              />
            )}
            <Card.Body className="text-center p-5">
              <div className="icon-wrapper icon-primary">
                <FiletypeCsv size={40} />
              </div>
              <h3 className="h4 fw-bold mb-3">Relatório Geral de Caixas</h3>
              <p className="text-muted mb-4 px-3">
                Relatório consolidado de todas as caixas registradas no sistema.
              </p>
              <div className="mt-auto">
                {downloadCsvRequest.loading ? (
                  <span className="progress-label">
                    {downloadCsvRequest.progress > 0 ? (
                      `Download em progresso: ${downloadCsvRequest.progress}%`
                    ) : (
                      <span className="starting-badge">Iniciando download...</span>
                    )}
                  </span>
                ) : (
                  <button type="button" className="btn btn-outline-primary rounded-pill px-4">
                    Baixar Relatório
                  </button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={4}>
          <Card
            className={`h-100 shadow-sm report-card ${
              downloadClientCsvRequest.loading ? 'loading' : ''
            }`}
          >
            {downloadClientCsvRequest.loading && (
              <ProgressBar
                animated={downloadClientCsvRequest.progress < 100}
                variant="success"
                now={downloadClientCsvRequest.progress}
                style={{
                  height: '6px',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                }}
              />
            )}
            <Card.Body className="text-center p-5 d-flex flex-column">
              <div className="icon-wrapper icon-success">
                <FiletypeCsv size={40} />
              </div>
              <h3 className="h4 fw-bold mb-3">Relatório de Caixas por Cliente</h3>
              <p className="text-muted mb-4 px-3">
                Relatório de caixas filtrado para um cliente específico.
              </p>

              <div className="mb-4">
                <Form.Select
                  className="form-select-lg border-2"
                  value={selectedClientId || ''}
                  onChange={(e) => setSelectedClientId(Number(e.target.value))}
                  disabled={fetchSubsidiariesRequest.loading || downloadClientCsvRequest.loading}
                >
                  <option value="">Selecione um cliente...</option>
                  {clients.map((client) => (
                    <option key={client.clientId} value={client.clientId}>
                      {client.name}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="mt-auto">
                {downloadClientCsvRequest.loading ? (
                  <span className="progress-label">
                    {downloadClientCsvRequest.progress > 0 ? (
                      `Download em progresso: ${downloadClientCsvRequest.progress}%`
                    ) : (
                      <span className="starting-badge text-success">Gerando arquivo...</span>
                    )}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-success rounded-pill px-5"
                    onClick={handleDownloadClientCsv}
                    disabled={!selectedClientId}
                  >
                    Baixar CSV
                  </button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ReportsGallery;
